import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import {
  useRequirementActivityQuery,
  type RequirementActivityEntry,
} from '@/lib/api/engagement-requirements';
import { CRITICALITY_LABEL, formatDate, plural } from './requirements-logic';

/**
 * The Activity list in an engagement requirement's side panel (T-0152).
 *
 * Reference: the activity block of `reqPanel()` in
 * `docs/requirement-library/prototype/selection.js`. Every entry comes
 * from the audit trail (`GET …/requirements/{id}/activity`): how the
 * requirement arrived and why, each reword with the old and new text,
 * criticality changes, removal and restore, with who and when. A change
 * made as one bulk action says so.
 *
 * The audit trail is written a moment after the change it records, so
 * after the row changes the list is fetched again, once straight away
 * (the cache key carries the row's `updated_at`) and once shortly after.
 */

/** How long after a change the list is fetched a second time. */
const SETTLE_REFETCH_MS = 1500;

const criticalityLabel = (value: string | null): string =>
  value != null && value in CRITICALITY_LABEL
    ? CRITICALITY_LABEL[value as keyof typeof CRITICALITY_LABEL]
    : (value ?? 'unknown');

/** "3 Oct 2026, 14:05" in the reader's time zone. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatDate(iso)}, ${hh}:${mm}`;
}

export interface ActivityDescription {
  /** What happened, in a few words. */
  title: string;
  /** Old and new text, shown in full under the title (a reword). */
  before: string | null;
  after: string | null;
  /** The reason recorded with the change, if any. */
  reason: string | null;
  /** How many requirements the same action covered, in words. */
  scope: string | null;
}

/** What one activity entry says. Pure, so the wording is unit tested. */
export function describeActivity(entry: RequirementActivityEntry): ActivityDescription {
  const base = { before: null, after: null, reason: entry.reason, scope: null };
  const bulkScope =
    entry.bulk && entry.bulk.count > 1
      ? `Part of a bulk change to ${plural(entry.bulk.count, 'requirement')}`
      : null;
  switch (entry.kind) {
    case 'created':
      return { ...base, title: 'Added manually' };
    case 'loaded': {
      // A discovery add records "Added from discovery" as its reason (C2-76):
      // the title says it, so the reason line would repeat it.
      const discovery = entry.load?.kind === 'discovery';
      return {
        ...base,
        title: discovery
          ? 'Added from discovery'
          : entry.load?.kind === 'spreadsheet'
            ? 'Imported from a client spreadsheet'
            : entry.load?.kind === 'library_match'
              ? 'Matched to the library from a client spreadsheet'
              : 'Loaded from the requirement library',
        reason: discovery ? null : entry.reason,
        scope:
          entry.bulk && entry.bulk.count > 1
            ? `One of ${plural(entry.bulk.count, 'requirement')} added together`
            : null,
      };
    }
    case 'reworded':
      return {
        ...base,
        title: 'Reworded',
        before: entry.from,
        after: entry.to,
        scope: bulkScope,
      };
    case 'module_changed':
      return {
        ...base,
        title: `Moved from ${entry.from ?? 'another module'} to ${entry.to ?? 'another module'}`,
        scope: bulkScope,
      };
    case 'client_area_changed':
      return {
        ...base,
        title:
          entry.from && entry.to
            ? `Client area changed from ${entry.from} to ${entry.to}`
            : entry.to
              ? `Client area set to ${entry.to}`
              : `Client area ${entry.from} cleared`,
        scope: bulkScope,
      };
    case 'criticality_changed':
      return {
        ...base,
        title:
          entry.from != null
            ? `Criticality changed from ${criticalityLabel(entry.from)} to ${criticalityLabel(entry.to)}`
            : `Criticality set to ${criticalityLabel(entry.to)}`,
        scope: bulkScope,
      };
    case 'subcategory_changed':
      return {
        ...base,
        title: `Moved from ${entry.from ?? 'another sub-category'} to ${entry.to ?? 'another sub-category'}`,
      };
    case 'removed':
      // `load` is set when undoing an import removed it (the load is the import).
      return {
        ...base,
        title: entry.load ? 'Removed by undoing the import' : 'Removed',
        scope: bulkScope,
      };
    case 'restored':
      return {
        ...base,
        title: entry.load ? 'Restored by a later load' : 'Restored',
        scope: bulkScope,
      };
    default:
      return { ...base, title: 'Changed' };
  }
}

export interface RequirementActivityProps {
  engagementId: string;
  requirementId: string;
  /** The row's `updated_at`: a new value means the history has grown. */
  revision: string;
}

export function RequirementActivity({
  engagementId,
  requirementId,
  revision,
}: RequirementActivityProps) {
  const query = useRequirementActivityQuery(engagementId, requirementId, revision);
  const { refetch } = query;

  // The audit entry for a change lands a moment after the change: look again.
  const firstRevision = React.useRef(revision);
  React.useEffect(() => {
    if (firstRevision.current === revision) return;
    firstRevision.current = revision;
    const timer = window.setTimeout(() => void refetch(), SETTLE_REFETCH_MS);
    return () => window.clearTimeout(timer);
  }, [revision, refetch]);

  if (query.isPending) {
    return (
      <p className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading activity…
      </p>
    );
  }
  if (query.isError) {
    return (
      <p role="alert" className="text-body-sm text-ink-700">
        The activity could not be loaded.{' '}
        <button
          type="button"
          className="font-medium underline underline-offset-[3px]"
          onClick={() => void refetch()}
        >
          Try again
        </button>
      </p>
    );
  }
  const entries = query.data.entries;
  if (entries.length === 0) {
    return <p className="text-body-sm text-ink-500">No activity has been recorded yet.</p>;
  }
  return (
    // `.p-log` in the prototype: a plain list with a small ink-300 dot per
    // entry, 12/18 text, and the "when · who" part in ink-500.
    <ol aria-label="Activity, newest first" className="flex flex-col gap-2">
      {entries.map((entry) => {
        const d = describeActivity(entry);
        const who = entry.actor ? (entry.actor.name ?? 'A user who has since left') : 'System';
        return (
          <li
            key={entry.id}
            className="relative pl-3.5 text-meta leading-[18px] text-ink-700 before:absolute before:left-0 before:top-[7px] before:h-[5px] before:w-[5px] before:rounded-full before:bg-ink-300"
          >
            {d.title}{' '}
            <span className="text-ink-500">
              · <time dateTime={entry.at}>{formatDateTime(entry.at)}</time> · {who}
            </span>
            {d.before != null && d.after != null ? (
              <div className="mt-1 rounded-card border border-ink-100 bg-paper-50 px-2.5 py-1.5">
                <span className="text-ink-500 line-through">{d.before}</span>
                <br />
                {d.after}
              </div>
            ) : null}
            {d.reason ? <div>Reason: {d.reason}</div> : null}
            {d.scope ? <div className="text-ink-500">{d.scope}</div> : null}
          </li>
        );
      })}
    </ol>
  );
}
